import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  type Node as RFNode,
  type NodeMouseHandler,
  type OnNodesChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { api, ApiError } from "../api";
import { buildGraph } from "../layout";
import type { Mindmap, NodePatch, TreeNode } from "../types";
import { MindNode } from "../components/MindNode";
import { NodePanel } from "../components/NodePanel";

function findNode(tree: TreeNode | null, id: string): TreeNode | null {
  if (!tree) return null;
  if (tree.id === id) return tree;
  for (const c of tree.children) {
    const r = findNode(c, id);
    if (r) return r;
  }
  return null;
}

function siblingAnchor(tree: TreeNode | null, id: string): { after_sibling_id?: string; before_sibling_id?: string } {
  if (!tree) return {};
  const node = findNode(tree, id);
  if (!node || !node.parent_id) return {};
  const parent = findNode(tree, node.parent_id);
  if (!parent) return {};
  const siblings = [...parent.children].sort((a, b) => a.sibling_rank.localeCompare(b.sibling_rank));
  const idx = siblings.findIndex((s) => s.id === id);
  if (idx > 0) return { after_sibling_id: siblings[idx - 1].id };
  if (idx === 0 && siblings.length > 1) return { before_sibling_id: siblings[1].id };
  return {};
}

type UndoOp =
  | { kind: "patch"; nodeId: string; prev: NodePatch; label: string }
  | { kind: "move"; nodeId: string; prevParentId: string; anchor: { after_sibling_id?: string; before_sibling_id?: string }; label: string }
  | { kind: "create"; nodeId: string; label: string };

const UNDO_LIMIT = 30;

const nodeTypes = { mindmap: MindNode };

export function Editor() {
  const { id: mindmapId } = useParams<{ id: string }>();
  const [meta, setMeta] = useState<Mindmap | null>(null);
  const [tree, setTree] = useState<TreeNode | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [undoStack, setUndoStack] = useState<UndoOp[]>([]);

  const pushUndo = useCallback((op: UndoOp) => {
    setUndoStack((s) => [...s, op].slice(-UNDO_LIMIT));
  }, []);

  const refreshTree = useCallback(async () => {
    if (!mindmapId) return;
    try {
      const data = await api.getTree(mindmapId);
      setMeta(data.mindmap);
      setTree(data.tree);
      setLoaded(true);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setError("Mindmap not found. Check the URL.");
        setLoaded(true);
      } else {
        setError(String(e));
      }
    }
  }, [mindmapId]);

  useEffect(() => {
    refreshTree();
  }, [refreshTree]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!editMode) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        handleUndo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, undoStack, tree]);

  const handleAddChild = async (parentId: string) => {
    if (!mindmapId) return;
    try {
      const created = await api.createNode(mindmapId, parentId, "new node");
      pushUndo({ kind: "create", nodeId: created.id, label: "add child" });
      await refreshTree();
    } catch (e) {
      setError(String(e));
    }
  };

  const handlePatchNode = async (node: TreeNode, patch: NodePatch, options: { recordUndo?: boolean } = {}) => {
    const recordUndo = options.recordUndo ?? true;
    try {
      if (recordUndo) {
        const prev: NodePatch = {};
        if (patch.title !== undefined) prev.title = node.title;
        if (patch.note !== undefined) prev.note = node.note;
        if (patch.checked !== undefined) prev.checked = node.checked;
        if (patch.color !== undefined) prev.color = node.color;
        if (patch.shape !== undefined) prev.shape = node.shape;
        if (patch.font_size !== undefined) prev.font_size = node.font_size;
        if (Object.keys(prev).length > 0) {
          pushUndo({ kind: "patch", nodeId: node.id, prev, label: `edit ${Object.keys(prev).join(",")}` });
        }
      }
      await api.patchNode(node.id, node.version, patch);
      await refreshTree();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError("Version conflict — reloading from server.");
      } else {
        setError(String(e));
      }
      await refreshTree();
    }
  };

  const handleDeleteNode = async (node: TreeNode) => {
    try {
      await api.deleteNode(node.id, node.version);
      // delete is not undoable — clear any undo entries that referenced this subtree
      const removed = new Set<string>();
      const collect = (n: TreeNode) => { removed.add(n.id); n.children.forEach(collect); };
      collect(node);
      setUndoStack((s) => s.filter((op) => !removed.has(op.nodeId)));
      await refreshTree();
      setSelectedNodeId(null);
    } catch (e) {
      setError(String(e));
    }
  };

  const handleMoveSibling = async (node: TreeNode, direction: "up" | "down") => {
    if (!tree || !node.parent_id) return;
    const parent = findNode(tree, node.parent_id);
    if (!parent) return;
    const siblings = [...parent.children].sort((a, b) => a.sibling_rank.localeCompare(b.sibling_rank));
    const idx = siblings.findIndex((s) => s.id === node.id);
    if (idx === -1) return;

    let anchor: { before_sibling_id?: string; after_sibling_id?: string };
    if (direction === "up") {
      if (idx === 0) return;
      anchor = { before_sibling_id: siblings[idx - 1].id };
    } else {
      if (idx === siblings.length - 1) return;
      anchor = { after_sibling_id: siblings[idx + 1].id };
    }
    try {
      const prevAnchor = siblingAnchor(tree, node.id);
      pushUndo({ kind: "move", nodeId: node.id, prevParentId: parent.id, anchor: prevAnchor, label: `move ${direction}` });
      await api.moveNode(node.id, node.version, parent.id, anchor);
      await refreshTree();
    } catch (e) {
      setError(String(e));
    }
  };

  const handleUndo = async () => {
    const op = undoStack[undoStack.length - 1];
    if (!op) return;
    setUndoStack((s) => s.slice(0, -1));
    try {
      if (op.kind === "patch") {
        const cur = findNode(tree, op.nodeId);
        if (!cur) { setError("undo: node missing"); return; }
        await api.patchNode(cur.id, cur.version, op.prev);
      } else if (op.kind === "move") {
        const cur = findNode(tree, op.nodeId);
        if (!cur) { setError("undo: node missing"); return; }
        await api.moveNode(cur.id, cur.version, op.prevParentId, op.anchor);
      } else if (op.kind === "create") {
        const cur = findNode(tree, op.nodeId);
        if (!cur) return;
        await api.deleteNode(cur.id, cur.version);
        if (selectedNodeId === op.nodeId) setSelectedNodeId(null);
      }
      await refreshTree();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError("Undo failed: version conflict — reloading.");
      } else {
        setError(`Undo failed: ${e}`);
      }
      await refreshTree();
    }
  };

  const { rfNodes, rfEdges } = useMemo(() => {
    const g = buildGraph(tree);
    const decorated = g.nodes.map((n) => {
      const treeNode = (n.data as { node: TreeNode }).node;
      return {
        ...n,
        data: {
          ...n.data,
          selected: treeNode.id === selectedNodeId,
          onToggle: editMode
            ? (tn: TreeNode) => handlePatchNode(tn, { checked: !tn.checked })
            : () => {},
        },
        draggable: editMode && treeNode.parent_id !== null,
      } as RFNode;
    });
    return { rfNodes: decorated, rfEdges: g.edges };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, selectedNodeId, editMode]);

  const onNodeClick: NodeMouseHandler = (_, n) => setSelectedNodeId(n.id);
  const onPaneClick = () => setSelectedNodeId(null);

  const onNodeDragStop: NodeMouseHandler = async (_, draggedNode) => {
    if (!editMode) return;
    if (!tree) return;
    const node = findNode(tree, draggedNode.id);
    if (!node || node.parent_id === null) return;
    const dropTarget = rfNodes
      .filter((n) => n.id !== draggedNode.id)
      .map((n) => {
        const dx = (n.position.x ?? 0) - (draggedNode.position.x ?? 0);
        const dy = (n.position.y ?? 0) - (draggedNode.position.y ?? 0);
        return { id: n.id, dist: Math.hypot(dx, dy) };
      })
      .sort((a, b) => a.dist - b.dist)[0];
    if (!dropTarget) {
      await refreshTree();
      return;
    }
    const candidate = findNode(tree, dropTarget.id);
    if (!candidate) return;
    const isDescendantOfNode = (cand: TreeNode): boolean => {
      if (cand.id === node.id) return true;
      return cand.children.some(isDescendantOfNode);
    };
    if (isDescendantOfNode(candidate) || candidate.id === node.parent_id) {
      await refreshTree();
      return;
    }
    try {
      const prevParentId = node.parent_id;
      const prevAnchor = siblingAnchor(tree, node.id);
      pushUndo({ kind: "move", nodeId: node.id, prevParentId, anchor: prevAnchor, label: "drag re-parent" });
      await api.moveNode(node.id, node.version, candidate.id);
      await refreshTree();
    } catch (e) {
      setError(String(e));
      await refreshTree();
    }
  };

  const onNodesChange: OnNodesChange = () => {
    // dagre re-layouts every render; no persistence of positions
  };

  if (!mindmapId) return <Centered>Missing mindmap id in URL.</Centered>;
  if (!loaded) return <Centered>loading…</Centered>;
  if (!meta) return <Centered>{error ?? "Mindmap not found."}</Centered>;

  const selectedNode = tree && selectedNodeId ? findNode(tree, selectedNodeId) : null;

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <header style={{
        position: "absolute", top: 0, left: 0, right: 0, zIndex: 50,
        padding: "10px 16px", background: "rgba(15,23,42,0.92)", color: "#e2e8f0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        fontSize: 13,
      }}>
        <div>
          <strong>{meta.name}</strong>
          <span style={{ color: "#94a3b8", marginLeft: 12, fontSize: 11 }}>id: {meta.id.slice(0, 8)}…</span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {editMode && (
            <button
              onClick={handleUndo}
              disabled={undoStack.length === 0}
              title={undoStack.length > 0 ? `Undo: ${undoStack[undoStack.length - 1].label} (${undoStack.length} step${undoStack.length === 1 ? "" : "s"})` : "Nothing to undo"}
              style={{
                ...hdrBtn,
                opacity: undoStack.length === 0 ? 0.4 : 1,
                cursor: undoStack.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              ↶ undo{undoStack.length > 0 ? ` (${undoStack.length})` : ""}
            </button>
          )}
          <button
            onClick={() => setEditMode((v) => !v)}
            style={{
              ...hdrBtn,
              background: editMode ? "#dc2626" : "transparent",
              borderColor: editMode ? "#dc2626" : "#475569",
              color: "#e2e8f0",
            }}
            title={editMode ? "Switch to read-only (lock layout)" : "Switch to edit mode (drag to re-parent, edit nodes)"}
          >
            {editMode ? "🔓 edit mode" : "🔒 read-only"}
          </button>
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              setError("link copied");
            }}
            style={hdrBtn}
          >
            copy link
          </button>
          <a href="/" style={{ ...hdrBtn, textDecoration: "none" }}>API docs</a>
        </div>
      </header>

      <div style={{ flex: 1, position: "relative", background: "#f8fafc", paddingTop: 40 }}>
        {error && (
          <div
            onClick={() => setError(null)}
            style={{
              position: "absolute", top: 48, left: 16, right: 16, padding: 10,
              background: "#fee2e2", color: "#991b1b", borderRadius: 4,
              zIndex: 100, fontSize: 12, cursor: "pointer",
            }}
          >
            {error} <span style={{ opacity: 0.6 }}>(click to dismiss)</span>
          </div>
        )}
        <ReactFlowProvider>
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={nodeTypes}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onNodeDragStop={onNodeDragStop}
            onNodesChange={onNodesChange}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            nodesConnectable={false}
            elementsSelectable
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} color="#cbd5e1" />
            <Controls showInteractive={false} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>

      {selectedNode && (
        <div style={{ width: 280, background: "#fff", borderLeft: "1px solid #e2e8f0", overflowY: "auto", paddingTop: 40 }}>
          <NodePanel
            node={selectedNode}
            onPatch={(p) => handlePatchNode(selectedNode, p as NodePatch)}
            onDelete={() => handleDeleteNode(selectedNode)}
            onAddChild={() => handleAddChild(selectedNode.id)}
            onMoveSibling={(dir) => handleMoveSibling(selectedNode, dir)}
            readOnly={!editMode}
          />
        </div>
      )}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      height: "100vh", color: "#475569", fontFamily: "system-ui, sans-serif",
    }}>
      {children}
    </div>
  );
}

const hdrBtn: React.CSSProperties = {
  background: "transparent",
  color: "#e2e8f0",
  border: "1px solid #475569",
  padding: "4px 10px",
  borderRadius: 3,
  cursor: "pointer",
  fontSize: 12,
};
